import Reading from "spectral-core/models/Reading";
import SpectralModel from "spectral-core/models/SpectralModel";
import {
  cultureTypeLabelPt,
  plantThirdLabelPt,
} from "spectral-core/cultureEnum";
import { getDiretoria } from "utils/diretoriaMap";
// cultureTypeLabelPt usado em exportNutroScanCSV

export interface PredictedParam {
  ParameterName: string;
  Value: number;
  ReferenceValue?: number | null;
}

/**
 * Faixa adequada por nutriente, vinda do modelo NIR cadastrado no SpectralCore.
 * Mesma fonte usada pelo app movel iSpectral2 (IclRegressionResults.razor) e
 * pelo PDF de relatorio do backend (SampleReportDocument.cs). Min/Max representam
 * os limites agronomicos de "adequado" — abaixo de min eh deficiencia, acima de max
 * eh excesso. Estrutura espelhada do RawModelJsonDto/ModelReferenceDto do backend.
 */
export interface ModelReferenceEntry {
  min: number | null;
  max: number | null;
  unit: string | null;
}

/**
 * Constroi indice de referencias agronomicas por nome de nutriente (case-insensitive,
 * normalizado) a partir da lista de SpectralModel retornada pelo endpoint /v2/models.
 * Cada modelo tem um campo `model` em JSON com MinReference/MaxReference/Unit.
 * Se mais de um modelo trouxer o mesmo parameterName, o primeiro com Min e Max
 * validos vence (suficiente pro caso atual: ICL roda com um conjunto unico de
 * modelos por nutriente). Mapeamento por cultura/aplicacao fica para iteracao
 * futura caso necessario.
 */
export function buildModelReferenceMap(
  models: SpectralModel[] | undefined | null
): Map<string, ModelReferenceEntry> {
  const map = new Map<string, ModelReferenceEntry>();
  if (!models || !models.length) return map;
  for (const m of models) {
    try {
      const key = normalizeNutrientName(m.parameterName || "");
      if (!key) continue;
      const parsed = JSON.parse(m.model || "{}");
      const min =
        typeof parsed.MinReference === "number" ? parsed.MinReference : null;
      const max =
        typeof parsed.MaxReference === "number" ? parsed.MaxReference : null;
      const unit =
        typeof parsed.Unit === "string" && parsed.Unit.trim()
          ? parsed.Unit.trim()
          : null;
      const existing = map.get(key);
      // Prioriza primeira entrada com Min e Max validos
      if (!existing || existing.min == null || existing.max == null) {
        map.set(key, { min, max, unit });
      }
    } catch {
      /* skip JSON invalido */
    }
  }
  return map;
}

export type NutrientStatus = "Deficiente" | "Adequado" | "Excesso" | "Indefinido";

/**
 * Classifica um valor contra a faixa adequada do modelo NIR cadastrado.
 * Mesma logica do PDF de relatorio backend (SampleReportDocument.cs:548-554) e
 * do app movel iSpectral2. Se min ou max nao estiverem cadastrados pra esse
 * nutriente, retorna Indefinido (pino cinza).
 */
export function classifyByModelRange(
  value: number | null | undefined,
  entry: ModelReferenceEntry | undefined
): NutrientStatus {
  if (
    value == null ||
    Number.isNaN(value) ||
    !entry ||
    entry.min == null ||
    entry.max == null
  ) {
    return "Indefinido";
  }
  if (value < entry.min) return "Deficiente";
  if (value > entry.max) return "Excesso";
  return "Adequado";
}

/* ─── Ordem canônica dos nutrientes (app/ICL) ─── */
export const NUTRIENT_ORDER = ["N", "P", "K", "Ca", "Mg", "S", "B", "Cu", "Fe", "Mn", "Zn"];
export const MACRO_NUTRIENTS = ["N", "P", "K", "Ca", "Mg", "S"];
export const MICRO_NUTRIENTS = ["B", "Cu", "Fe", "Mn", "Zn"];
/* Parâmetros extras específicos para Cana-de-açúcar */
export const CANA_EXTRA_PARAMS = ["ATR", "AR", "ART", "BRIX", "POL", "FIBRA", "UMIDADE"];

/** Retorna a lista de parâmetros esperados para a cultura selecionada. */
export function paramsForCulture(culture: string | undefined | null): string[] {
  const base = [...MACRO_NUTRIENTS, ...MICRO_NUTRIENTS];
  if (culture && /cana/i.test(culture)) {
    return [...base, ...CANA_EXTRA_PARAMS];
  }
  return base;
}

export function sortByNutrientOrder(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const ia = NUTRIENT_ORDER.indexOf(a);
    const ib = NUTRIENT_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/* ─── Ordem canônica de fenologia (Soja) ─── */
const GROWTH_STAGE_ORDER = [
  "V1-V4", "V5-Vn",
  "R1", "R2", "R3", "R4", "R5", "R6", "R7", "R8",
];

/** Normaliza e consolida o código do estádio fenológico.
 *  - Remove descrições depois de ":" ou "."
 *  - Agrupa V1..V4 → "V1-V4"; V5..Vn e "Vn" → "V5-Vn"
 *  - R5.1, R5.2, R4/R5.1 → "R5"
 *  - Mantém R1..R8
 *  - Demais valores (Milho, Veg, etc) ficam como estão (apenas trim + caps do prefixo V/R)
 */
export function growthStageCode(raw: string): string {
  if (!raw) return raw;
  let s = raw.trim();

  // Primeiro: se começar com "V1–Vn" (em-dash) ou similar, já é um range consolidado
  s = s.replace(/[–—]/g, "-"); // en/em-dash → hyphen

  // Remove descrição depois de ":" ou "." (mas preserva casos tipo "R5.1")
  const colon = s.indexOf(":");
  if (colon > 0) s = s.substring(0, colon).trim();

  // R5 com sub-stages: "R5", "R5.1", "R5.2"..., "R4/R5.1" → bucket "R5"
  if (/(^|\/)R5(\.\d+)?$/i.test(s)) return "R5";

  // R1..R8 puro
  const rMatch = s.match(/^R([1-8])$/i);
  if (rMatch) return `R${rMatch[1]}`;

  // V1..V4 → V1-V4
  if (/^V[1-4]$/i.test(s)) return "V1-V4";

  // V5+, Vn, V1-Vn, v5-Vn → V5-Vn
  if (/^V([5-9]|[1-9]\d+|N)$/i.test(s)) return "V5-Vn";
  if (/^V1?-V?N$/i.test(s)) return "V5-Vn";
  if (/^V5-VN$/i.test(s)) return "V5-Vn";

  // Outros (Milho "VT", "Grão pastoso", "Veg", etc): devolve com trim, sem tratar
  // Quebra em "." só depois de esgotar os casos R5.X
  const dot = s.indexOf(".");
  if (dot > 0) s = s.substring(0, dot).trim();

  return s;
}

export function sortByGrowthStageOrder<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ia = GROWTH_STAGE_ORDER.findIndex((g) => a.label.toLowerCase() === g.toLowerCase());
    const ib = GROWTH_STAGE_ORDER.findIndex((g) => b.label.toLowerCase() === g.toLowerCase());
    if (ia === -1 && ib === -1) return a.label.localeCompare(b.label);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/* ─── Ordem canônica de parte da planta ─── */
const PLANT_PART_ORDER = ["Inferior", "Médio", "Superior", "Todas"];

export function sortByPlantPartOrder<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ia = PLANT_PART_ORDER.findIndex((p) => a.label.toLowerCase() === p.toLowerCase());
    const ib = PLANT_PART_ORDER.findIndex((p) => b.label.toLowerCase() === p.toLowerCase());
    if (ia === -1 && ib === -1) return a.label.localeCompare(b.label);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/* ─── Normalização de nomes de nutrientes ─── */
const NUTRIENT_NORMALIZE: Record<string, string> = {};

/** Remove trailing pontos, espaços e apóstrofos de nomes de nutrientes (ex: "N'" → "N"). */
export function normalizeNutrientName(raw: string): string {
  const trimmed = raw.replace(/[\s'.]+$/g, "").trim();
  return NUTRIENT_NORMALIZE[trimmed] || trimmed;
}

/* ─── Filtragem global NutroScan ─── */

/** Data mínima de leituras válidas (entrega dos equipamentos). */
const MIN_DATE = new Date(2025, 10, 14); // 14/11/2025

/**Data da alteração de coletas possuírem informações adicionais */
const CULTURE_REQUIRED_DATE = new Date(2026, 8, 1); // 01/09/2026

/** Filtra leituras para NutroScan:
 *  - A partir de 14/11/2025 (entrega dos equipamentos)
 *  - Exclui testes de São Carlos (userSite)
 *  - Opcional: apenas leituras médias (nome contém "Média" ou "Media")
 */
export function filterNutroScanReadings(
  readings: Reading[],
  onlyMedia: boolean = false
): Reading[] {
  return readings.filter((r) => {
    // Cutoff global: leituras anteriores à entrega dos equipamentos são descartadas
    const dt = parseReadingDate(r);
    if (!dt || dt < MIN_DATE) return false;

    // Filtro opcional: apenas leituras médias
    if (onlyMedia) {
      if (r.type === "Related") return false;

      if (r.type === "acquisition") {
        const culture = String(r.culture ?? "").trim();
        const isLegacyAcquisition = dt < CULTURE_REQUIRED_DATE && !culture;

        if (!isLegacyAcquisition) {
          const hasCollectTime = parseCollectDate(r) !== null;

          const sampleName = String(r.name ?? "").trim();
          const hasMediaInName = /m[eé]dia/i.test(sampleName);

          if (!hasCollectTime && !hasMediaInName) {
            return false;
          }
        }
      }
    }

        // Excluir testes de São Carlos
        const site = (r.userSite || "").toLowerCase();
        const user = (r.userName || "").toLowerCase();
        if (site.includes("são carlos") || site.includes("sao carlos")) return false;
        if (user.includes("são carlos") || user.includes("sao carlos")) return false;

        return true;
      });
    }

function parseReadingDate(r: Reading): Date | null {
  try {
    const raw = r.dateTime;
    if (!raw) return null;
    const [datePart, timePart] = raw.split(" ");
    if (!datePart) return null;
    const [day, month, year] = datePart.split("/").map(Number);
    if (!year || !month || !day) return null;
    let h = 0,
      m = 0,
      s = 0;
    if (timePart) {
      const t = timePart.split(":");
      h = Number(t[0]) || 0;
      m = Number(t[1]) || 0;
      s = Number(t[2]) || 0;
    }
    return new Date(year, month - 1, day, h, m, s);
  } catch {
    return null;
  }
}

function parseCollectDate(r: Reading): Date | null {
  if (!r.collectTime || String(r.collectTime).trim() === "") return null;
  try {
    const raw = String(r.collectTime).trim();
    if (raw.includes("/")) {
      const [datePart, timePart] = raw.split(" ");
      const [day, month, year] = datePart.split("/").map(Number);
      let h = 0,
        m = 0,
        s = 0;
      if (timePart) {
        const t = timePart.split(":");
        h = Number(t[0]) || 0;
        m = Number(t[1]) || 0;
        s = Number(t[2]) || 0;
      }
      return new Date(year, month - 1, day, h, m, s);
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export function parsePredictedValues(reading: Reading): PredictedParam[] {
  try {
    const j = JSON.parse(reading.predictedValues || "[]");
    if (!Array.isArray(j)) return [];
    // Normaliza nomes e deduplica (pega o primeiro em caso de nome duplicado)
    const seen = new Map<string, PredictedParam>();
    for (const p of j) {
      const name = normalizeNutrientName(p.ParameterName || "");
      if (!seen.has(name)) {
        seen.set(name, { ...p, ParameterName: name });
      }
    }
    return Array.from(seen.values());
  } catch {
    return [];
  }
}

export function aggregateNutroScanOverview(readings: Reading[]) {
  const totalReadings = readings.length;
  let lastSync: Date | null = null;
  let tempSum = 0;
  let tempN = 0;
  let collectDeltaSumMin = 0;
  let collectDeltaN = 0;

  const byMonth: Record<string, number> = {};
  const byCulture: Record<string, number> = {};
  const byGrowthStage: Record<string, number> = {};
  const byPlantThird: Record<string, number> = {};
  const byWashed: Record<string, number> = { Sim: 0, Não: 0, "Não informado": 0 };
  const cultivateCount: Record<string, number> = {};

  for (const r of readings) {
    if (r.syncedToCloudAt) {
      const d = new Date(r.syncedToCloudAt);
      if (!lastSync || d > lastSync) lastSync = d;
    }
    if (typeof r.temperature === "number" && !isNaN(r.temperature)) {
      tempSum += r.temperature;
      tempN++;
    }
    const dt = parseReadingDate(r);
    const ct = parseCollectDate(r);
    if (dt && ct) {
      const diffMin = (dt.getTime() - ct.getTime()) / 60000;
      if (diffMin >= 0 && diffMin < 60 * 24 * 30) {
        collectDeltaSumMin += diffMin;
        collectDeltaN++;
      }
    }
    if (dt) {
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    }
    const cultLabel =
      cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
    byCulture[cultLabel] = (byCulture[cultLabel] || 0) + 1;

    const gsRaw = (r.growthStage || "").trim() || "—";
    const gs = gsRaw === "—" ? gsRaw : growthStageCode(gsRaw);
    byGrowthStage[gs] = (byGrowthStage[gs] || 0) + 1;

    const pt = plantThirdLabelPt(r.plantThird) || "—";
    byPlantThird[pt] = (byPlantThird[pt] || 0) + 1;

    if (r.washed === true) byWashed["Sim"]++;
    else if (r.washed === false) byWashed["Não"]++;
    else byWashed["Não informado"]++;

    const cv = (r.cultivate || "").trim() || "—";
    cultivateCount[cv] = (cultivateCount[cv] || 0) + 1;
  }

  const distinctCultivars = Object.keys(cultivateCount).filter((k) => k !== "—");
  const distinctCultivarsCount = distinctCultivars.length;
  const topCultivars = distinctCultivars
    .map((k) => [k, cultivateCount[k]] as [string, number])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return {
    totalReadings,
    lastSync,
    avgTemperature: tempN ? tempSum / tempN : null,
    avgCollectToAnalysisMinutes: collectDeltaN
      ? collectDeltaSumMin / collectDeltaN
      : null,
    byMonth,
    byCulture,
    byGrowthStage,
    byPlantThird,
    byWashed,
    topCultivars,
    distinctCultivarsCount,
  };
}

export function parameterNamesUnion(readings: Reading[]): string[] {
  const s = new Set<string>();
  for (const r of readings) {
    for (const p of parsePredictedValues(r)) {
      if (p.ParameterName) s.add(p.ParameterName);
    }
  }
  return sortByNutrientOrder(Array.from(s));
}

export function averageParameterValue(
  readings: Reading[],
  parameterName: string
): number | null {
  let sum = 0;
  let n = 0;
  for (const r of readings) {
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      sum += p.Value;
      n++;
    }
  }
  return n ? sum / n : null;
}

export function averageParameterByMonth(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byM: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const dt = parseReadingDate(r);
    if (!dt) continue;
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      if (!byM[key]) byM[key] = { sum: 0, n: 0 };
      byM[key].sum += p.Value;
      byM[key].n++;
    }
  }
  return Object.keys(byM)
    .sort()
    .map((k) => ({
      label: k,
      value: byM[k].n ? byM[k].sum / byM[k].n : 0,
    }));
}

export function averageParameterByGrowthStage(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byG: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const gsRaw = (r.growthStage || "").trim() || "—";
    const gs = gsRaw === "—" ? gsRaw : growthStageCode(gsRaw);
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      if (!byG[gs]) byG[gs] = { sum: 0, n: 0 };
      byG[gs].sum += p.Value;
      byG[gs].n++;
    }
  }
  const items = Object.entries(byG)
    .map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }));
  return sortByGrowthStageOrder(items);
}

export function averageParameterByPlantThird(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byP: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const pt = plantThirdLabelPt(r.plantThird) || "—";
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      if (!byP[pt]) byP[pt] = { sum: 0, n: 0 };
      byP[pt].sum += p.Value;
      byP[pt].n++;
    }
  }
  return sortByPlantPartOrder(
    Object.entries(byP).map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }))
  );
}

export function averageParameterByWashed(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byW: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const w = r.washed === true ? "Sim" : r.washed === false ? "Não" : "Não informado";
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      if (!byW[w]) byW[w] = { sum: 0, n: 0 };
      byW[w].sum += p.Value;
      byW[w].n++;
    }
  }
  return Object.entries(byW)
    .map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function averageParameterByCollectDelta(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const buckets: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const dt = parseReadingDate(r);
    const ct = parseCollectDate(r);
    if (!dt || !ct) continue;
    const diffMin = (dt.getTime() - ct.getTime()) / 60000;
    if (diffMin < 0 || diffMin >= 60 * 24 * 30) continue;
    let bucket: string;
    if (diffMin < 30) bucket = "< 30 min";
    else if (diffMin < 60) bucket = "30–60 min";
    else if (diffMin < 120) bucket = "1–2 h";
    else if (diffMin < 360) bucket = "2–6 h";
    else if (diffMin < 1440) bucket = "6–24 h";
    else bucket = "> 24 h";
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (p && typeof p.Value === "number") {
      if (!buckets[bucket]) buckets[bucket] = { sum: 0, n: 0 };
      buckets[bucket].sum += p.Value;
      buckets[bucket].n++;
    }
  }
  const order = ["< 30 min", "30–60 min", "1–2 h", "2–6 h", "6–24 h", "> 24 h"];
  return order
    .filter((k) => buckets[k])
    .map((k) => ({ label: k, value: buckets[k].sum / buckets[k].n }));
}

export function countReadingsWithLabResult(readings: Reading[]) : number {
  let count = 0;
  for (const reading of readings) {
    const predictedValues = parsePredictedValues(reading);
    const predictedWithLabResult = predictedValues.find(hasLaboratoryResult);
    if (predictedWithLabResult)
      count++;
  }
  return count;
}

function hasLaboratoryResult(predictedValue: PredictedParam) : boolean {
  return predictedValue &&
  typeof predictedValue.ReferenceValue === "number"; 
}

/** Dados para gráfico comparativo NIR vs Lab (barras lado a lado). */
export function nirVsLabChartData(
  readings: Reading[]
): { nutrient: string; nir: number; lab: number }[] {
  const out: { nutrient: string; nir: number; lab: number }[] = [];
  const names = parameterNamesUnion(readings);
  for (const name of names) {
    const nir = averageParameterValue(readings, name);
    const lab = averageReferenceValue(readings, name);
    if (nir != null) {
      out.push({ nutrient: name, nir, lab: lab ?? 0 });
    }
  }
  return out;
}

/** Export de dados NutroScan para CSV (download no browser). */
export function exportNutroScanCSV(readings: Reading[]) {
  const params = parameterNamesUnion(readings);
  const header = [
    "Cultura", "Cultivar", "Fenologia", "Parte", "Lavada",
    "Coleta", "Análise", "Amostra", "Talhão", "Temperatura",
    "Latitude", "Longitude", "Mahalanobis",
    ...params.map((p) => `NIR_${p}`),
    ...params.map((p) => `Ref_${p}`),
  ];

  const rows: string[][] = [];
  for (const r of readings) {
    const preds = parsePredictedValues(r);
    let lat = "", lng = "";
    try {
      if (r.geoLocation && r.geoLocation.length > 5) {
        const geo = JSON.parse(r.geoLocation);
        lat = String(geo.Latitude ?? "");
        lng = String(geo.Longitude ?? "");
      }
    } catch { /* skip */ }
    const row: string[] = [
      cultureTypeLabelPt(r.culture, r.customCultureName),
      r.cultivate ?? "",
      r.growthStage ?? "",
      plantThirdLabelPt(r.plantThird),
      r.washed === true ? "Sim" : r.washed === false ? "Não" : "",
      r.collectTime ?? "",
      r.dateTime ?? "",
      r.sampleCode ?? "",
      r.plot ?? "",
      String(r.temperature ?? ""),
      lat, lng,
      String(r.mahalanobisDistance ?? ""),
      ...params.map((p) => {
        const v = preds.find((x) => x.ParameterName === p);
        return v ? String(v.Value ?? "") : "";
      }),
      ...params.map((p) => {
        const v = preds.find((x) => x.ParameterName === p);
        return v && v.ReferenceValue != null ? String(v.ReferenceValue) : "";
      }),
    ];
    rows.push(row);
  }

  const csvContent =
    "\uFEFF" +
    header.join(";") +
    "\n" +
    rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(";")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `NutroScan_Export_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function averageReferenceValue(
  readings: Reading[],
  parameterName: string
): number | null {
  let sum = 0;
  let n = 0;
  for (const r of readings) {
    const p = parsePredictedValues(r).find(
      (x) => x.ParameterName === parameterName
    );
    if (
      p &&
      p.ReferenceValue !== undefined &&
      p.ReferenceValue !== null &&
      typeof p.ReferenceValue === "number"
    ) {
      sum += p.ReferenceValue;
      n++;
    }
  }
  return n ? sum / n : null;
}

/* ─── Médias de referência (Lab) por dimensão — para correlações ─── */

export function averageReferenceByMonth(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byM: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const dt = parseReadingDate(r);
    if (!dt) continue;
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    const p = parsePredictedValues(r).find((x) => x.ParameterName === parameterName);
    if (p && p.ReferenceValue != null && typeof p.ReferenceValue === "number") {
      if (!byM[key]) byM[key] = { sum: 0, n: 0 };
      byM[key].sum += p.ReferenceValue;
      byM[key].n++;
    }
  }
  return Object.keys(byM).sort().map((k) => ({ label: k, value: byM[k].n ? byM[k].sum / byM[k].n : 0 }));
}

export function averageReferenceByGrowthStage(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byG: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const gsRaw = (r.growthStage || "").trim() || "—";
    const gs = gsRaw === "—" ? gsRaw : growthStageCode(gsRaw);
    const p = parsePredictedValues(r).find((x) => x.ParameterName === parameterName);
    if (p && p.ReferenceValue != null && typeof p.ReferenceValue === "number") {
      if (!byG[gs]) byG[gs] = { sum: 0, n: 0 };
      byG[gs].sum += p.ReferenceValue;
      byG[gs].n++;
    }
  }
  return sortByGrowthStageOrder(
    Object.entries(byG).map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }))
  );
}

export function averageReferenceByPlantThird(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byP: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const pt = plantThirdLabelPt(r.plantThird) || "—";
    const p = parsePredictedValues(r).find((x) => x.ParameterName === parameterName);
    if (p && p.ReferenceValue != null && typeof p.ReferenceValue === "number") {
      if (!byP[pt]) byP[pt] = { sum: 0, n: 0 };
      byP[pt].sum += p.ReferenceValue;
      byP[pt].n++;
    }
  }
  return sortByPlantPartOrder(
    Object.entries(byP).map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }))
  );
}

export function averageReferenceByWashed(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const byW: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const w = r.washed === true ? "Sim" : r.washed === false ? "Não" : "Não informado";
    const p = parsePredictedValues(r).find((x) => x.ParameterName === parameterName);
    if (p && p.ReferenceValue != null && typeof p.ReferenceValue === "number") {
      if (!byW[w]) byW[w] = { sum: 0, n: 0 };
      byW[w].sum += p.ReferenceValue;
      byW[w].n++;
    }
  }
  return Object.entries(byW)
    .map(([k, v]) => ({ label: k, value: v.n ? v.sum / v.n : 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function averageReferenceByCollectDelta(
  readings: Reading[],
  parameterName: string
): { label: string; value: number }[] {
  const buckets: Record<string, { sum: number; n: number }> = {};
  for (const r of readings) {
    const dt = parseReadingDate(r);
    const ct = parseCollectDate(r);
    if (!dt || !ct) continue;
    const diffMin = (dt.getTime() - ct.getTime()) / 60000;
    if (diffMin < 0 || diffMin >= 60 * 24 * 30) continue;
    let bucket: string;
    if (diffMin < 30) bucket = "< 30 min";
    else if (diffMin < 60) bucket = "30–60 min";
    else if (diffMin < 120) bucket = "1–2 h";
    else if (diffMin < 360) bucket = "2–6 h";
    else if (diffMin < 1440) bucket = "6–24 h";
    else bucket = "> 24 h";
    const p = parsePredictedValues(r).find((x) => x.ParameterName === parameterName);
    if (p && p.ReferenceValue != null && typeof p.ReferenceValue === "number") {
      if (!buckets[bucket]) buckets[bucket] = { sum: 0, n: 0 };
      buckets[bucket].sum += p.ReferenceValue;
      buckets[bucket].n++;
    }
  }
  const order = ["< 30 min", "30–60 min", "1–2 h", "2–6 h", "6–24 h", "> 24 h"];
  return order.filter((k) => buckets[k]).map((k) => ({ label: k, value: buckets[k].sum / buckets[k].n }));
}

export interface MapPinRow {
  name: string;
  position: [number, number];
  detail: string;
  color: string;
}

export function readingsToMapPins(
  readings: Reading[],
  nutrientForStatus?: string | null,
  source: "nir" | "lab" = "nir",
  modelReferenceMap?: Map<string, ModelReferenceEntry>
): MapPinRow[] {
  const out: MapPinRow[] = [];
  for (const item of readings) {
    try {
      if (!item.geoLocation || item.geoLocation.length < 10) continue;
      const { Latitude, Longitude } = JSON.parse(item.geoLocation);
      const name = item.name || item.sampleCode || "—";
      let color = "#9e9e9e";
      let detail = "";
      if (nutrientForStatus) {
        const p = parsePredictedValues(item).find(
          (x) => x.ParameterName === nutrientForStatus
        );
        if (p) {
          // Classificacao baseada nos Min/Max do modelo NIR cadastrado
          // (mesma fonte do app movel e do PDF de relatorio do backend).
          // NIR usa o valor predito; Lab usa o ReferenceValue (valor medido em laboratorio).
          // Ambos sao comparados contra a MESMA faixa adequada do modelo.
          const valueToClassify =
            source === "lab" ? (p.ReferenceValue ?? null) : (p.Value ?? null);
          const refKey = normalizeNutrientName(nutrientForStatus);
          const refEntry = modelReferenceMap?.get(refKey);
          const c = classifyByModelRange(valueToClassify, refEntry);
          const valStr =
            valueToClassify != null ? valueToClassify.toFixed(2) : "—";
          const unitStr = refEntry?.unit ? ` ${refEntry.unit}` : "";
          detail = `${nutrientForStatus}: ${valStr}${unitStr} (${c})`;
          color =
            c === "Deficiente"
              ? "#e53935"
              : c === "Excesso"
                ? "#fb8c00"
                : c === "Adequado"
                  ? "#43a047"
                  : "#9e9e9e";
        }
      } else {
        const preds = parsePredictedValues(item);
        detail = preds
          .slice(0, 3)
          .map((x) => `${x.ParameterName}: ${x.Value?.toFixed?.(2) ?? x.Value}`)
          .join(", ");
      }
      out.push({
        name,
        position: [Latitude, Longitude],
        detail,
        color,
      });
    } catch {
      /* skip */
    }
  }
  return out;
}

/** Heatmap simplificado: quando há ReferenceValue, classifica por ±10% em torno da referência. */
export function classifyNutrientRow(
  p: PredictedParam
): "Deficiente" | "Adequado" | "Excesso" | "Indefinido" {
  const ref = p.ReferenceValue;
  if (ref === undefined || ref === null || ref === 0)
    return "Indefinido";
  const low = ref * 0.9;
  const high = ref * 1.1;
  if (p.Value < low) return "Deficiente";
  if (p.Value > high) return "Excesso";
  return "Adequado";
}

type ClassCounts = { Deficiente: number; Adequado: number; Excesso: number; Indefinido: number };

export function heatmapCounts(
  readings: Reading[],
  modelReferenceMap?: Map<string, ModelReferenceEntry>
) {
  const nutrients = new Set<string>();
  const nirGrid: Record<string, ClassCounts> = {};
  const labGrid: Record<string, ClassCounts> = {};

  for (const r of readings) {
    for (const p of parsePredictedValues(r)) {
      const name = p.ParameterName;
      if (!name) continue;
      nutrients.add(name);
      const refEntry = modelReferenceMap?.get(normalizeNutrientName(name));
      // NIR: valor predito pelo equipamento vs faixa adequada do modelo
      if (!nirGrid[name]) nirGrid[name] = { Deficiente: 0, Adequado: 0, Excesso: 0, Indefinido: 0 };
      const nirClass = classifyByModelRange(p.Value, refEntry);
      nirGrid[name][nirClass]++;
      // Lab: valor medido em laboratorio vs MESMA faixa adequada do modelo
      if (!labGrid[name]) labGrid[name] = { Deficiente: 0, Adequado: 0, Excesso: 0, Indefinido: 0 };
      const labClass =
        p.ReferenceValue != null && p.ReferenceValue !== 0
          ? classifyByModelRange(p.ReferenceValue, refEntry)
          : "Indefinido";
      labGrid[name][labClass]++;
    }
  }
  return {
    nutrients: sortByNutrientOrder(Array.from(nutrients)),
    grid: nirGrid,
    labGrid,
  };
}

/* ─── Relatório por CDM / mês / cultura ─── */

export interface ReportRow {
  userName: string;
  diretoria: string;
  month: string;
  lastSync: string;
  soja: number;
  milho: number;
  cafe: number;
  cana: number;
  algodao: number;
  outras: number;
  demonstracao: number;
  total: number;
}

const CULTURE_BUCKETS: Record<string, keyof Omit<ReportRow, "userName" | "diretoria" | "month" | "lastSync" | "total">> = {
  "Soja": "soja",
  "Milho": "milho",
  "Café": "cafe",
  "Cana-de-açúcar": "cana",
  "Algodão": "algodao",
  "Demonstração": "demonstracao",
};

function cultureBucket(label: string): keyof Omit<ReportRow, "userName" | "diretoria" | "month" | "lastSync" | "total"> {
  return CULTURE_BUCKETS[label] || "outras";
}

export function aggregateReport(readings: Reading[]): ReportRow[] {
  const map = new Map<string, ReportRow>();

  for (const r of readings) {
    const dt = parseReadingDate(r);
    if (!dt) continue;

    const user = (r.userName || "").trim() || "—";
    const dir = getDiretoria(r.userName);
    const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    const key = `${user}||${monthKey}`;

    if (!map.has(key)) {
      map.set(key, {
        userName: user,
        diretoria: dir,
        month: monthKey,
        lastSync: "",
        soja: 0, milho: 0, cafe: 0, cana: 0, algodao: 0, outras: 0, demonstracao: 0,
        total: 0,
      });
    }
    const row = map.get(key)!;

    // Última sincronização
    if (r.syncedToCloudAt) {
      const syncStr = new Date(r.syncedToCloudAt).toLocaleString();
      if (!row.lastSync || syncStr > row.lastSync) row.lastSync = syncStr;
    }

    // Cultura
    const cultLabel = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
    const bucket = cultureBucket(cultLabel);
    row[bucket]++;
    row.total++;
  }

  return Array.from(map.values()).sort((a, b) => {
    const s = a.diretoria.localeCompare(b.diretoria);
    if (s !== 0) return s;
    const u = a.userName.localeCompare(b.userName);
    if (u !== 0) return u;
    return a.month.localeCompare(b.month);
  });
}

export function exportReportCSV(rows: ReportRow[]) {
  const header = [
    "Diretoria", "Nome CDM", "Mês", "Última sincronização",
    "Soja", "Milho", "Café", "Cana-de-açúcar", "Algodão",
    "Outras Culturas", "Demonstração", "Total",
  ];

  const csvRows = rows.map((r) => [
    r.diretoria, r.userName, r.month, r.lastSync,
    String(r.soja || ""), String(r.milho || ""), String(r.cafe || ""),
    String(r.cana || ""), String(r.algodao || ""), String(r.outras || ""),
    String(r.demonstracao || ""), String(r.total),
  ]);

  const csvContent =
    "\uFEFF" +
    header.join(";") + "\n" +
    csvRows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(";")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `NutroScan_Relatorio_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
